import React, { useState, useEffect } from 'react';
import { HelpingHand as PrayingHands, Heart, Search, AlertCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import AdminLayout from '../components/AdminLayout';

interface PrayerRequest {
  id: string;
  name: string;
  request: string;
  is_public: boolean;
  is_anonymous: boolean;
  created_at: string;
  user_id: string;
}

export default function AdminPrayers() {
  const [prayers, setPrayers] = useState<PrayerRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchPrayers();
  }, []);

  async function fetchPrayers() {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('prayer_requests')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPrayers(data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const filteredPrayers = prayers.filter(prayer =>
    prayer.request.toLowerCase().includes(searchTerm.toLowerCase()) ||
    prayer.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <AdminLayout title="Pedidos de Oração">
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-brand-teal flex items-center gap-2">
            <PrayingHands className="h-5 w-5" />
            Pedidos de Oração
          </h2>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar pedidos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
            />
          </div>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 text-red-600 rounded-lg flex items-start">
            <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Nome</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Pedido</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Status</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Data</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-teal"></div>
                    </div>
                  </td>
                </tr>
              ) : filteredPrayers.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                    Nenhum pedido de oração encontrado
                  </td>
                </tr>
              ) : (
                filteredPrayers.map((prayer) => (
                  <tr key={prayer.id} className="border-b border-gray-200">
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-brand-light text-brand-teal flex items-center justify-center font-medium">
                          <Heart className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="font-medium">{prayer.name}</p>
                          {prayer.is_anonymous && (
                            <span className="text-xs text-gray-500">Anônimo</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-gray-600 line-clamp-2">{prayer.request}</p>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        prayer.is_public
                          ? 'bg-green-100 text-green-600'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {prayer.is_public ? 'Público' : 'Privado'}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-gray-500">
                      {new Date(prayer.created_at).toLocaleDateString('pt-BR')}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}